import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Error desconocido';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [validError, setValidError] = useState(null);
  const [emailHint, setEmailHint] = useState(null);

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/reset-password/validate`, { params: { token } });
        if (!active) return;
        setEmailHint(r.data?.email || null);
        setValidating(false);
      } catch (e) {
        if (!active) return;
        setValidError(formatApiErrorDetail(e?.response?.data?.detail) || 'Enlace inválido');
        setValidating(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (pw !== pw2) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success('Contraseña actualizada');
      setTimeout(() => navigate('/login?reset=ok'), 1800);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0b1220' }} data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-2xl p-7 shadow-2xl" style={{ background: '#101a2e', border: '1px solid #1e2a4a' }}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34, 211, 238, 0.12)' }}>
            <Lock className="w-5 h-5" style={{ color: '#22d3ee' }} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Restablecer contraseña</h1>
            <p className="text-xs text-slate-400 mt-0.5">XÉNICOS</p>
          </div>
        </div>

        {validating ? (
          <div className="py-10 flex flex-col items-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <p className="text-sm">Verificando enlace…</p>
          </div>
        ) : validError ? (
          <div className="py-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ background: 'rgba(239, 68, 68, 0.15)' }}>
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-sm font-medium text-red-300 mb-1">No se puede usar este enlace</p>
            <p className="text-xs text-slate-400">{validError}</p>
            <Link to="/login" className="inline-block mt-5 text-sm text-blue-400 hover:underline" data-testid="reset-back-to-login">
              Volver a iniciar sesión
            </Link>
          </div>
        ) : done ? (
          <div className="py-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ background: 'rgba(16, 185, 129, 0.18)' }}>
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            </div>
            <p className="text-sm font-medium text-emerald-300 mb-1">Contraseña actualizada</p>
            <p className="text-xs text-slate-400">Redirigiendo al login…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {emailHint && (
              <p className="text-xs text-slate-400">
                Cuenta: <span className="text-slate-200 font-medium">{emailHint}</span>
              </p>
            )}

            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 uppercase tracking-wide" htmlFor="pw">Nueva contraseña</label>
              <div className="relative">
                <input
                  id="pw"
                  type={showPw ? 'text' : 'password'}
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm text-white border focus:outline-none focus:border-blue-500"
                  style={{ background: '#0b1428', borderColor: '#1e2a4a' }}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                  data-testid="reset-pw-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-200"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 uppercase tracking-wide" htmlFor="pw2">Confirmar contraseña</label>
              <input
                id="pw2"
                type={showPw ? 'text' : 'password'}
                value={pw2}
                onChange={e => setPw2(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white border focus:outline-none focus:border-blue-500"
                style={{ background: '#0b1428', borderColor: '#1e2a4a' }}
                autoComplete="new-password"
                data-testid="reset-pw2-input"
                required
              />
              {pw && pw2 && pw !== pw2 && (
                <p className="text-xs text-red-400 mt-1">Las contraseñas no coinciden</p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="reset-submit-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Establecer contraseña
            </button>

            <p className="text-[11px] text-slate-500 text-center pt-2">
              Este enlace caduca 1 hora después de su generación y solo puede usarse una vez.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
